import * as React from 'react';

import { cn } from '@/lib/utils';

import {
  FLEX,
  FLEX_ALIGN as FLEX_ALIGN_SCALE,
  FLEX_JUSTIFY as FLEX_JUSTIFY_SCALE,
  GAP_SCALE,
} from './tokens';

const DIRECTION_CLASSNAMES = {
  row: FLEX.flexRow,
  column: FLEX.flexCol,
};

interface CreateFlexPrimitiveOptions {
  direction?: 'row' | 'column';
  defaultAlign?: keyof typeof FLEX_ALIGN_SCALE;
  defaultJustify?: keyof typeof FLEX_JUSTIFY_SCALE;
  defaultWrap?: boolean;
}

interface FlexComponentProps extends React.HTMLAttributes<HTMLElement> {
  as?: React.ElementType;
  gap?: keyof typeof GAP_SCALE;
  align?: keyof typeof FLEX_ALIGN_SCALE;
  justify?: keyof typeof FLEX_JUSTIFY_SCALE;
  wrap?: boolean;
  fullWidth?: boolean;
  children?: React.ReactNode;
}

function createFlexPrimitive(options: CreateFlexPrimitiveOptions = {}) {
  const {
    direction = 'column',
    defaultAlign = 'stretch',
    defaultJustify = 'start',
    defaultWrap = false,
  } = options;
  const directionClasses = DIRECTION_CLASSNAMES[direction];

  const FlexComponent = React.forwardRef<HTMLElement, FlexComponentProps>(
    (
      {
        as: Component = 'div',
        gap = 'md',
        align = defaultAlign,
        justify = defaultJustify,
        wrap = defaultWrap,
        fullWidth = false,
        className,
        children,
        ...props
      },
      ref
    ) => (
      <Component
        ref={ref}
        className={cn(
          directionClasses,
          GAP_SCALE[gap],
          FLEX_ALIGN_SCALE[align],
          FLEX_JUSTIFY_SCALE[justify],
          wrap ? 'flex-wrap' : 'flex-nowrap',
          fullWidth && 'w-full',
          className
        )}
        {...props}
      >
        {children}
      </Component>
    )
  );

  FlexComponent.displayName = 'FlexPrimitive';

  return FlexComponent;
}

export { createFlexPrimitive };
